import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';
import api from '../services/api';

import { IOption } from '../components/SelectSync';
import { useToast } from './toast';
import getResponseErrors from '../utils/getResponseErrors';

interface IParceiro {
  ParCodigo: number;
  ParRazaoSocial: string;
}

export interface IBeneficio {
  id: number;
  PbnCodigo: number;
  PbnParCodigo: number;
  PbnNome: string;
  PbnValor: number;
  PbnValorPorDependente: boolean;
  PbnCarencia: number;
  PbnZeraCarencia: boolean;
  PbnApenasNovo: boolean;
  PbnDataInicio: Date;
  PbnDataFim: Date;
  PbnAtivo: boolean;
  PbnChave: string;
  PbnContrato: string;
  created_at: Date;
  updated_at: Date;
  parceiro: IParceiro;
}

interface BeneficioContextData {
  loading: boolean;
  optionsBeneficio: IOption[];
  beneficio?: IBeneficio[];
  buscaOptionsBeneficio(): Promise<void>;
  limparBeneficio(): void;
}

const BeneficioContext = createContext<BeneficioContextData>(
  {} as BeneficioContextData,
);

const BeneficioProvider: React.FC = ({ children }) => {
  const [loading, setLoading] = useState(false);

  const { addToast } = useToast();

  const [optionsBeneficio, setOptionsBeneficio] = useState<IOption[]>(() => {
    const optionsBeneficios = localStorage.getItem(
      '@AgendaFacil:optionsBeneficio',
    );
    if (optionsBeneficios) return JSON.parse(optionsBeneficios);

    return [] as IOption[];
  });

  const [beneficio, setBeneficio] = useState<IBeneficio[]>(() => {
    const beneficios = localStorage.getItem('@AgendaFacil:beneficio');
    if (beneficios) return JSON.parse(beneficios);

    return undefined;
  });

  const buscaOptionsBeneficio = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<IBeneficio[]>(
        `/parceiros/listaBeneficios`,
      );

      const options = response.data.map((op: IBeneficio) => ({
        value: op.PbnCodigo.toString(),
        label: op.PbnNome,
      }));

      localStorage.setItem(
        '@AgendaFacil:optionsBeneficio',
        JSON.stringify(options),
      );
      localStorage.setItem(
        '@AgendaFacil:beneficio',
        JSON.stringify(response.data),
      );
      setOptionsBeneficio(options);
      setBeneficio(response.data);
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível carregar Benefícios',
        type: 'error',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    async function load(): Promise<void> {
      const tokenLocal = localStorage.getItem('@AgendaFacil:token');
      if (tokenLocal) {
        buscaOptionsBeneficio;
      }
    }
    load();
  }, [buscaOptionsBeneficio]);

  const limparBeneficio = useCallback(() => {
    localStorage.removeItem('@AgendaFacil:optionsBeneficio');
    localStorage.removeItem('@AgendaFacil:beneficio');
    setOptionsBeneficio([]);
    setBeneficio([]);
  }, []);

  return (
    <BeneficioContext.Provider
      value={{
        loading,
        buscaOptionsBeneficio,
        optionsBeneficio,
        beneficio,
        limparBeneficio,
      }}
    >
      {children}
    </BeneficioContext.Provider>
  );
};

function useBeneficio(): BeneficioContextData {
  const context = useContext(BeneficioContext);

  if (!context) {
    throw new Error('useBeneficio deve ser usado com o BeneficioProvider');
  }

  return context;
}

export { BeneficioProvider, useBeneficio };
